import { navigationUrls } from '../../navigation/navigation-urls';
import { PortalCommunicationOperator } from '../../utils/communication-operator.util';
import {
  ExternalFooterLink,
  FooterConfig,
  InternalFooterLink,
} from './footer.types';

const external = (
  labelKey: string,
  href: string,
  documentType?: ExternalFooterLink['documentType']
): ExternalFooterLink => ({
  kind: 'external',
  labelKey,
  href,
  ...(documentType ? { documentType } : {}),
});

const internal = (labelKey: string, path: string): InternalFooterLink => ({
  kind: 'internal',
  labelKey,
  path,
});

const swedishPaths = navigationUrls.swedish;

const SE_FOOTER_CONFIG: FooterConfig = {
  addresses: [
    ['Bäverns Gränd 17, 753 81', 'Uppsala, Sverige'],
    ['Solnavägen 3H, 113 63', 'Stockholm, Sverige'],
  ],
  companyName: 'GlobalConnect AB',
  socialLinks: [
    {
      icon: 'facebook',
      href: 'https://www.facebook.com/globalconnect.se/',
    },
    {
      icon: 'linkedin',
      href: 'https://www.linkedin.com/company/globalconnect-a-s',
    },
    {
      icon: 'instagram',
      href: 'https://www.instagram.com/globalconnect_sverige/',
    },
  ],
  linkGroups: [
    {
      titleKey: 'footer.se.menus.globalConnect',
      links: [
        external(
          'footer.se.links.aboutUs',
          'https://foretag.globalconnect.se/om-oss/'
        ),
        external(
          'footer.se.links.euProjects',
          'https://globalconnect.se/eu-finansierade-projket/'
        ),
        external(
          'footer.se.links.privacyPolicy',
          'https://globalconnect.se/privat-integritetspolicy/'
        ),
        external(
          'footer.se.links.cookiePolicy',
          'https://globalconnect.se/cookiepolicy/'
        ),
        external(
          'footer.se.links.career',
          'https://www.globalconnectgroup.com/career'
        ),
        external(
          'footer.se.links.press',
          'https://www.globalconnect.se/om-globalconnect/press#/'
        ),
      ],
    },
    {
      titleKey: 'footer.se.menus.purchaseInformation',
      links: [
        external(
          'footer.se.links.terms',
          'https://globalconnect.se/allmanna-villkor/'
        ),
        internal(
          'footer.se.links.fiberInterest',
          `/${swedishPaths.fiberInterestFull}`
        ),
        internal(
          'footer.se.links.leisureHomeServices',
          `/${swedishPaths.leisureHomes}`
        ),
      ],
    },
    {
      titleKey: 'footer.se.menus.customerService',
      links: [
        external(
          'footer.se.links.faq',
          'https://globalconnect.se/fragor-och-svar/'
        ),
        external(
          'footer.se.links.contact',
          'https://globalconnect.se/kontakta-oss/'
        ),
        external(
          'footer.se.links.serviceStatus',
          'https://globalconnect.se/driftinformation/'
        ),
        external(
          'footer.se.links.landowners',
          'https://globalconnect.se/markagare/'
        ),
        external(
          'footer.se.links.accessibility',
          'https://globalconnect.se/tillganglighet/'
        ),
      ],
    },
  ],
};

const FI_FOOTER_CONFIG: FooterConfig = {
  addresses: [['Maistraatinportti 1', '00240 Helsinki', 'Finland']],
  companyName: 'GlobalConnect',
  socialLinks: [
    {
      icon: 'facebook',
      href: 'https://www.facebook.com/GlobalConnectSuomi/',
    },
    {
      icon: 'youtube',
      href: 'https://www.youtube.com/@GlobalConnectSuomi',
    },
    {
      icon: 'linkedin',
      href: 'https://www.linkedin.com/company/globalconnect-a-s',
    },
    {
      icon: 'instagram',
      href: 'https://www.instagram.com/globalconnect.fi/',
    },
  ],
  linkGroups: [
    {
      titleKey: 'footer.fi.menus.globalConnect',
      links: [
        external(
          'footer.fi.links.aboutUs',
          'https://yrityksille.globalconnect.fi/tietoja-globalconnectista'
        ),
        external(
          'footer.fi.links.career',
          'https://globalconnect.jobylon.com/'
        ),
        external(
          'footer.fi.links.news',
          'https://globalconnect.fi/ajankohtaista/'
        ),
        external(
          'footer.fi.links.media',
          'https://www.mynewsdesk.com/fi/globalconnect'
        ),
        external(
          'footer.fi.links.sustainability',
          'https://www.globalconnectgroup.com/sustainability'
        ),
        external(
          'footer.fi.links.businessCustomers',
          'https://yrityksille.globalconnect.fi/'
        ),
      ],
    },
    {
      titleKey: 'footer.fi.menus.support',
      links: [
        external(
          'footer.fi.links.customerService',
          'https://globalconnect.fi/asiakaspalvelu/'
        ),
        external(
          'footer.fi.links.faq',
          'https://globalconnect.fi/usein-kysytyt-kysymykset/'
        ),
        external(
          'footer.fi.links.cancellationForm',
          'https://globalconnect.fi/peruutus/'
        ),
        external(
          'footer.fi.links.serviceStatus',
          'https://globalconnect.fi/hairiotiedotteet/'
        ),
        external(
          'footer.fi.links.cableInformation',
          'https://globalconnect.fi/kaapelinaytto-kaapeleiden-sijaintitiedot/'
        ),
        external(
          'footer.fi.links.accessibility',
          'https://globalconnect.fi/saavutettavuus/'
        ),
      ],
    },
    {
      titleKey: 'footer.fi.menus.termsAndPricing',
      links: [
        external(
          'footer.fi.links.priceList',
          'https://cms.globalconnect.net/app/uploads/sites/2/2026/08/GC_Hinnasto_2026.pdf',
          'pdf'
        ),
        external(
          'footer.fi.links.salesTerms',
          'https://cms.globalconnect.net/app/uploads/sites/2/2026/08/GC_Myyntiehdot_2026.pdf',
          'pdf'
        ),
        external(
          'footer.fi.links.serviceTerms',
          'https://cms.globalconnect.net/app/uploads/sites/2/2026/08/GC_palveluehdot_2026.pdf',
          'pdf'
        ),
        external(
          'footer.fi.links.privacyPolicy',
          'https://cms.globalconnect.net/app/uploads/sites/2/2026/05/GlobalConnect-Privacy-Policy-v-2.3-FI-2026-05-01.pdf',
          'pdf'
        ),
        external(
          'footer.fi.links.cookiePolicy',
          'https://cms.globalconnect.net/app/uploads/sites/2/2026/09/Cookie-Policy-2.0-FIN-RCC-approved-27-05-2026.pdf',
          'pdf'
        ),
      ],
    },
  ],
};

export const FOOTER_CONFIG: Record<PortalCommunicationOperator, FooterConfig> =
  {
    [PortalCommunicationOperator.GlobalConnectSe]: SE_FOOTER_CONFIG,
    [PortalCommunicationOperator.GlobalConnectFi]: FI_FOOTER_CONFIG,
  };
